import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, LogOut, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CollectionEditor } from "@/admin/CollectionEditor";
import { MediaTab } from "@/admin/MediaTab";
import { PersonalTab } from "@/admin/PersonalTab";
import { JamesTab } from "@/admin/JamesTab";
import { CodesTab } from "@/admin/CodesTab";
import { MemoriesTab, PrivacyTab, SettingsTab } from "@/admin/OtherTabs";

const TABS = [["members", "Family Members"], ["timeline", "Timeline"], ["photos", "Photos"], ["businesses", "Businesses"], ["investments", "Investments"], ["associations", "Associations"],
  ["memorials", "In Memoriam"], ["voices", "Voices & Videos"], ["places", "Map Places"], ["archive", "Archive"], ["governance", "Governance"], ["memories", "Memories"], ["personal", "Personal Pages"], ["james", "James's Page"],
  ["codes", "Codes"], ["privacy", "Privacy"], ["settings", "Settings"]];

export const AdminLogin = () => {
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [ticket, setTicket] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      if (ticket) { await api.post("/auth/totp", { ticket, code }); nav("/admin"); return; }
      const r = await api.post("/auth/login", f);
      if (r.data?.need_totp) setTicket(r.data.ticket); else nav("/admin");
    } catch (er) { setErr(errText(er)); }
  };
  return (
    <div className="min-h-screen grid place-items-center px-6 bg-[radial-gradient(ellipse_at_top,#163B29,#0B1D14_70%)]" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm card-gold p-8 grid gap-4">
        <Logo size={44} />
        <h1 className="font-display text-3xl">Family admin</h1>
        {!ticket ? (
          <>
            <div><label className="label">Email</label><input className="field" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="admin-email-input" /></div>
            <div><label className="label">Password</label><input className="field" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="admin-password-input" /></div>
          </>
        ) : <div><label className="label">Code from your authenticator app</label><input className="field tracking-[0.3em]" value={code} onChange={(e) => setCode(e.target.value)} required inputMode="numeric" data-testid="admin-totp-input" /></div>}
        {err && <p className="text-sm text-[#F2B8A0]" data-testid="admin-login-error">{err}</p>}
        <button className="btn-gold justify-center" data-testid="admin-login-btn">{ticket ? "Verify" : "Sign in"}</button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [me, setMe] = useState(undefined);
  const [tab, setTab] = useState("members");
  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null)); }, []);
  if (me === undefined) return null;
  if (me === null) return <Navigate to="/admin/login" replace />;
  const preview = () => { sessionStorage.setItem("public_preview", "1"); window.open("/?preview=1", "_blank"); setTimeout(() => sessionStorage.removeItem("public_preview"), 1500); };
  const panic = async () => { if (!window.confirm("Lock the members area and sign everyone out now?")) return; try { const r = await api.post("/admin/panic"); toast.success(`Members area locked. ${r.data.signed_out} sessions signed out.`); } catch (e) { toast.error(errText(e)); } };
  const body = tab === "photos" ? <MediaTab /> : tab === "personal" ? <PersonalTab /> : tab === "james" ? <JamesTab /> : tab === "codes" ? <CodesTab /> : tab === "privacy" ? <PrivacyTab /> : tab === "settings" ? <SettingsTab /> : tab === "memories" ? <MemoriesTab /> : <CollectionEditor key={tab} coll={tab} />;
  return (
    <div className="min-h-screen bg-[#0B1D14]" data-testid="admin-page">
      <header className="sticky top-0 z-40 bg-[#0B1D14]/90 backdrop-blur border-b border-[#C9A24B]/20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-3">
          <Logo size={30} /><span className="font-display tracking-[0.2em] text-sm hidden sm:inline">ADMIN</span>
          <div className="ml-auto flex items-center gap-2">
            <button className="btn-ghost btn-sm" onClick={preview} data-testid="admin-public-preview-btn"><Eye size={14} /> Public preview</button>
            <button className="btn-sm rounded-full bg-[#B4442C] text-white px-3 py-2 inline-flex items-center gap-1" onClick={panic} data-testid="admin-panic-btn"><ShieldAlert size={14} /> <span className="hidden sm:inline">Panic</span></button>
            <button className="lb-btn !w-9 !h-9" onClick={async () => { await api.post("/auth/logout"); window.location.href = "/admin/login"; }} data-testid="admin-logout-btn"><LogOut size={14} /></button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid gap-8 lg:grid-cols-[220px_1fr]">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:sticky lg:top-24 self-start pb-2" data-testid="admin-tabs">
          {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`shrink-0 text-left px-4 py-2.5 rounded-lg text-sm transition-colors ${tab === k ? "bg-[#C9A24B] text-[#0B1D14]" : "text-[#F6EFE0]/85 hover:bg-[#C9A24B]/10"}`} data-testid={`admin-tab-${k}`}>{l}</button>)}
        </nav>
        <main className="min-w-0">{body}</main>
      </div>
    </div>
  );
}
